/**
 * CashFlowForecast — widget grafico dei flussi in entrata previsti
 * a orizzonte 15gg → 6 mesi.
 *
 * Sorgenti:
 *  - fatture emesse con residuo>0 → data_incasso_prevista
 *  - attività Gantt non ancora fatturate → data_fatturazione_prevista + N gg
 *
 * Uso: <CashFlowForecast />  (auto-load, admin only via API)
 */
import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis,
  Tooltip, Legend, CartesianGrid,
} from "recharts";
import { TrendingUp, Wallet, Coins } from "lucide-react";
import api from "@/api";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(n || 0));

const HORIZONS = [
  { d: 15, l: "15 gg" },
  { d: 30, l: "1 mese" },
  { d: 60, l: "2 mesi" },
  { d: 90, l: "3 mesi" },
  { d: 180, l: "6 mesi" },
];

const BUCKETS = [
  { k: "week", l: "Settimane" },
  { k: "biweek", l: "Quindicine" },
  { k: "month", l: "Mesi" },
];

export default function CashFlowForecast() {
  const [horizon, setHorizon] = useState(180);
  const [bucket, setBucket] = useState("biweek");

  const { data, isLoading } = useQuery({
    queryKey: ["cash-flow-forecast", horizon, bucket],
    queryFn: () =>
      api.get(`/fatture/cash-flow/forecast`, {
        params: { horizon_days: horizon, bucket },
      }).then((r) => r.data),
    staleTime: 30_000,
  });

  const chart = (data?.buckets || []).map((b) => ({
    ...b,
    label: b.label,
  }));

  return (
    <div className="bg-white border border-slate-200 rounded-sm" data-testid="cash-flow-forecast">
      <div className="p-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="overline">Previsione cassa</div>
          <h3 className="font-display text-xl font-bold flex items-center gap-2">
            <Wallet className="w-5 h-5 text-slate-500" /> Cash Flow Forecast
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            <span className="text-emerald-700">Entrate</span> (fatture + Gantt) meno <span className="text-rose-700">Uscite</span> (costi variabili, fissi, rate subappalti).
            Rosso = previsto, nero = pagato.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="inline-flex border border-slate-300 rounded-sm overflow-hidden">
            {HORIZONS.map((h) => (
              <button
                key={h.d}
                onClick={() => setHorizon(h.d)}
                data-testid={`cf-horizon-${h.d}`}
                className={`px-3 py-1.5 text-xs font-semibold ${horizon === h.d ? "bg-slate-800 text-white" : "bg-white hover:bg-slate-50"}`}
              >
                {h.l}
              </button>
            ))}
          </div>
          <select
            value={bucket}
            onChange={(e) => setBucket(e.target.value)}
            data-testid="cf-bucket"
            className="px-2 py-1.5 border border-slate-300 rounded-sm text-xs bg-white"
          >
            {BUCKETS.map((b) => <option key={b.k} value={b.k}>{b.l}</option>)}
          </select>
        </div>
      </div>

      {/* Totali sintetici */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2 p-4 border-b border-slate-200">
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-sm">
          <div className="text-[10px] uppercase tracking-widest text-blue-700 font-bold flex items-center gap-1">
            <TrendingUp className="w-3 h-3" /> Entrate confermate
          </div>
          <div className="font-display font-black text-lg text-blue-900 mt-1">{eur(data?.confermato || 0)}</div>
        </div>
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm">
          <div className="text-[10px] uppercase tracking-widest text-amber-700 font-bold flex items-center gap-1">
            <Coins className="w-3 h-3" /> Entrate previste (Gantt)
          </div>
          <div className="font-display font-black text-lg text-amber-900 mt-1">{eur(data?.previsto || 0)}</div>
        </div>
        <div className="p-3 bg-violet-50 border border-violet-200 rounded-sm">
          <div className="text-[10px] uppercase tracking-widest text-violet-700 font-bold">
            Crediti fiscali da ritenuta
          </div>
          <div className="font-display font-black text-lg text-violet-900 mt-1">
            {eur(data?.ritenute_credito || 0)}
          </div>
          <div className="text-[10px] text-violet-700 mt-1">
            Non inclusi nel saldo bancario
          </div>
        </div>
        <div className="p-3 bg-slate-900 text-white rounded-sm">
          <div className="text-[10px] uppercase tracking-widest opacity-80 font-bold">Uscite pagate (nero)</div>
          <div className="font-display font-black text-lg mt-1">{eur(data?.uscite_effettive || 0)}</div>
        </div>
        <div className="p-3 bg-rose-100 border border-rose-200 rounded-sm">
          <div className="text-[10px] uppercase tracking-widest text-rose-700 font-bold">Uscite previste (rosso)</div>
          <div className="font-display font-black text-lg text-rose-900 mt-1">{eur(data?.uscite_previste || 0)}</div>
        </div>
        <div className={`p-3 border rounded-sm ${(data?.netto || 0) >= 0 ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-300"}`}>
          <div className={`text-[10px] uppercase tracking-widest font-bold ${(data?.netto || 0) >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
            Netto (Entrate − Uscite)
          </div>
          <div className={`font-display font-black text-lg mt-1 ${(data?.netto || 0) >= 0 ? "text-emerald-900" : "text-rose-900"}`}>
            {eur(data?.netto || 0)}
          </div>
        </div>
      </div>

      {/* Grafico */}
      <div className="p-4">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-slate-400">Caricamento…</div>
        ) : chart.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-400">Nessun flusso previsto nell'orizzonte selezionato.</div>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={chart} margin={{ top: 10, right: 16, left: 8, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="label" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={60} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => new Intl.NumberFormat("it-IT", { notation: "compact" }).format(v)} />
              <Tooltip formatter={(v) => eur(v)} labelStyle={{ fontWeight: "bold" }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="confermato" stackId="in" fill="#3b82f6" name="Entrate confermate" />
              <Bar dataKey="previsto" stackId="in" fill="#f59e0b" name="Entrate previste" />
              <Bar dataKey="uscite_effettive" stackId="out" fill="#1e293b" name="Uscite pagate" />
              <Bar dataKey="uscite_previste" stackId="out" fill="#f43f5e" name="Uscite previste" />
              <Line type="monotone" dataKey="cumulativo" stroke="#10b981" strokeWidth={2} name="Netto cumulativo" dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Tabella periodi */}
      <div className="overflow-x-auto border-t border-slate-200">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-slate-600 uppercase tracking-widest">
            <tr>
              <th className="text-left p-2">Periodo</th>
              <th className="text-right p-2">Entrate conf.</th>
              <th className="text-right p-2">Entrate prev.</th>
              <th className="text-right p-2 text-violet-700">Ritenute (credito fiscale)</th>
              <th className="text-right p-2 text-slate-900">Uscite pagate</th>
              <th className="text-right p-2 text-rose-700">Uscite previste</th>
              <th className="text-right p-2 font-bold">Netto</th>
              <th className="text-right p-2 font-bold">Cumulativo</th>
            </tr>
          </thead>
          <tbody>
            {chart.map((b) => (
              <tr key={b.start} className="border-t border-slate-100" data-testid={`cf-row-${b.start}`}>
                <td className="p-2 font-mono">{b.label}</td>
                <td className="p-2 text-right font-mono text-blue-800">{eur(b.confermato)}</td>
                <td className="p-2 text-right font-mono text-amber-800">{eur(b.previsto)}</td>
                <td className="p-2 text-right font-mono text-violet-800">{eur(b.ritenute_credito)}</td>
                <td className="p-2 text-right font-mono text-slate-900 font-semibold">{eur(b.uscite_effettive)}</td>
                <td className="p-2 text-right font-mono text-rose-700 font-semibold">{eur(b.uscite_previste)}</td>
                <td className={`p-2 text-right font-mono font-bold ${b.netto >= 0 ? "text-emerald-800" : "text-rose-800"}`}>{eur(b.netto)}</td>
                <td className={`p-2 text-right font-mono font-bold ${b.cumulativo >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{eur(b.cumulativo)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
